import { useTheme } from "@emotion/react";
import { Typography, useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import React, { useContext, useState } from "react";
import { useQueryClient } from "react-query";
import { ApiDataContext } from "../../../contexts/ApiDataContext";
import useDialog from "../../../hooks/dialog/useDialog";
import { useFormField } from "../../../hooks/forms/FormHooks";
import { useManagementTextField } from "../../../hooks/forms/ManagementHooks";
import { deleteProject, getProjectPrice, simpleUpdateProject } from "../../../services/api/api";
import CustomAutoComplete from "../../shared/autoCompletes/CustomAutoComplete";
import CustomLoadingButton from "../../shared/button/CustomLoadingButton";
import CustomDialog from "../../shared/dialog/CustomDialog";
import { FormatMoney, FormatPower } from "../../shared/format/Formats";
import CustomTextField from "../../shared/textFields/CustomTextField";
import FormatedCustomTextField from "../../shared/textFields/FormatedCustomTextField";
import PosVendaModal from "./PosVendaModal";
import FactoryIcon from "@mui/icons-material/Factory";
import { api } from "../../../services/api/api";

export default function SimpleEdit({
  projectData,
  clearAllFields,
  client,
  isDisabled,
  setDisabled,
  initializeLoadingToast,
  updateLoadingToast,
}) {
  const { apiData } = useContext(ApiDataContext);
  const queryClient = useQueryClient();

  const [requestLoading, setRequestLoading] = useState(false);

  const handleUpdateClick = async () => {
    let updateToastId;

    try {
      setRequestLoading(true);
      setDisabled(true);
      updateToastId = initializeLoadingToast({
        text: `Atualizando Informações Básicas...`,
      });

      const { status } = await simpleUpdateProject(projectData.id, {
        projectName: projectName.value,
        clientId: client.value.id,
        powerDistributionCompanyId: powerDistributionCompany.value.id,
        engineerId: engineer?.value?.id,
        power: power.value,
        price: price.value,
      });

      if (status === 200) {
        updateLoadingToast({
          id: updateToastId,
          text: `Informações Básicas Atualizadas com sucesso`,
          type: "success",
        });
      }
    } catch (error) {
      updateLoadingToast({
        id: updateToastId,
        text:
          error?.response?.data?.message ??
          `Erro ao atualizar Informações Básicas, tente novamente`,
        type: "error",
      });
    } finally {
      setRequestLoading(false);
      setDisabled(false);
    }
  };

  const { reset: resetProjectName, ...projectName } = useManagementTextField(projectData?.name);

  const { reset: resetPDC, ...powerDistributionCompany } = useFormField(
    "autocompleteChangeInitialValue",
    projectData?.powerDistributionCompanies,
    false
  );

  const { reset: reset, ...engineer } = useFormField(
    "autocompleteChangeInitialValue",
    projectData?.engineers,
    false
  );

  const [powerValue, setPowerValue] = useState(projectData?.power ?? "");
  const [projectPrice, setProjectPrice] = useState(projectData?.price ?? "");

  const handlePowerChange = async (event) => {
    const _power = event.target.value;
    setPowerValue(_power);
    const clientValue = client.value;
    const isInternalClient = clientValue.internalClient === 1;
    const { data } = await getProjectPrice({
      power: _power,
      isInternalClient,
      tableId: clientValue.priceTableId,
    });
    setProjectPrice(data);
  };

  const power = {
    value: powerValue,
    onChange: handlePowerChange,
  };

  const { reset: resetPrice, ...price } = useManagementTextField(projectPrice);

  const [deleteRequestLoading, setDeleteRequestLoading] = useState(false);

  const handleDeleteClick = async () => {
    let deleteToastId;
    setDeleteRequestLoading(true);
    setDisabled(true);
    try {
      deleteToastId = initializeLoadingToast({
        text: `Excluindo Projeto...`,
      });

      const { status } = await deleteProject(projectData.id);

      if (status === 200) {
        clearAllFields();
        queryClient.invalidateQueries({ queryKey: ["userProvidences"] });
        queryClient.invalidateQueries({ queryKey: ["homeBaseQuery"] });
        queryClient.invalidateQueries({ queryKey: ["filteredProjectsQuery"] });

        updateLoadingToast({
          id: deleteToastId,
          text: `Projeto Excluído com sucesso`,
          type: "success",
        });
      }
    } catch (error) {
      updateLoadingToast({
        id: deleteToastId,
        text: error.response.data.message ?? `Erro ao Excluir Projeto, tente novamente`,
        type: "error",
      });
    } finally {
      setDeleteRequestLoading(false);
      setDisabled(false);
      handleClickClose();
    }
  };

  const { open, handleClickOpen, handleClickClose } = useDialog();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Pós-Vendas Modal
  const [posVendaModalOpen, setPosVendaModalOpen] = useState(false);
  const [posVendaSending, setPosVendaSending] = useState(false);
  const [sentToPosVenda, setSentToPosVenda] = useState(false);

  const handlePosVendaClick = () => {
    setPosVendaModalOpen(true);
  };

  const handlePosVendaClose = () => {
    setPosVendaModalOpen(false);
  };

  const handlePosVendaConfirm = async (customPlantName) => {
    let toastId;
    
    try {
      setPosVendaSending(true);
      toastId = initializeLoadingToast({
        text: "Cadastrando usina no Pós-Vendas...",
      });

      const { data } = await api.post(`/projects/${projectData.id}/send-to-posvenda`, {
        customPlantName,
      });

      if (data.success) {
        setSentToPosVenda(true);
        
        // Invalida queries para atualizar comentários
        queryClient.invalidateQueries({ queryKey: ["projectComments"] });
        
        updateLoadingToast({
          id: toastId,
          text: data.message || "Usina cadastrada com sucesso no Pós-Vendas",
          type: "success",
        });
        handlePosVendaClose();
      }
    } catch (error) {
      const errorData = error?.response?.data;
      
      // Se já existe, marca como enviado mas mostra aviso
      if (errorData?.alreadyExists) {
        setSentToPosVenda(true);
        updateLoadingToast({
          id: toastId,
          text: errorData.message || "Projeto já cadastrado no Pós-Vendas",
          type: "warning",
        });
        handlePosVendaClose();
      } else {
        updateLoadingToast({
          id: toastId,
          text: errorData?.message || "Erro ao cadastrar usina no Pós-Vendas",
          type: "error",
        });
      }
    } finally {
      setPosVendaSending(false);
    }
  };

  const isInternalProject = client?.value?.internalClient === 1;

  return (
    <Box sx={{ width: "100%" }}>
      <CustomDialog
        open={open}
        handleClickClose={handleClickClose}
        title="Excluir Projeto"
        contentText="As informações do projeto serão excluídas e não poderão ser recuperadas."
        requestLoading={deleteRequestLoading}
        handleConfirmationClick={handleDeleteClick}
        confirmationClickText="Excluir"
        requestLoadingText="Excluindo..."
      />

      <PosVendaModal
        open={posVendaModalOpen}
        onClose={handlePosVendaClose}
        projectData={projectData}
        onConfirm={handlePosVendaConfirm}
        loading={posVendaSending}
      />

      {projectData ? (
        <Box
          sx={{
            width: "100%",
            borderRadius: 3,
            display: "flex",
            mt: 1,
            justifyContent: "space-between",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              width: "100%",
              color: 102030,
              display: "flex",
              justifyContent: "space-between",
              flexDirection: isSmallScreen ? "column" : "row",
            }}
          >
            <Box
              sx={{
                width: isSmallScreen ? "100%" : "48%",
                display: "flex",
                justifyContent: "space-between",
                flexDirection: "column",
              }}
            >
              <CustomTextField
                params={projectName}
                disabled={isDisabled}
                label="Nome do Cliente"
                errorLabel="Nome Incorreto"
                maxLength={50}
              />
              <CustomAutoComplete
                disabled={isDisabled}
                params={client}
                options={apiData.clients}
                label="Integrador"
                width="100%"
              />
              <CustomAutoComplete
                disabled={isDisabled}
                params={powerDistributionCompany}
                options={apiData.powerDistributionCompanies}
                label="Concessionária"
                width="100%"
              />
            </Box>
            <Box
              sx={{
                width: isSmallScreen ? "100%" : "48%",
                display: "flex",
                justifyContent: "space-between",
                flexDirection: "column",
              }}
            >
              <CustomAutoComplete
                disabled={isDisabled}
                params={engineer}
                options={apiData.engineers}
                label="Engenheiro"
                width="100%"
              />
              <FormatedCustomTextField
                params={power}
                disabled={isDisabled}
                label="Potência"
                errorLabel="Potência Incorreta"
                inputcomponent={FormatPower}
                required={false}
              />
              <FormatedCustomTextField
                params={price}
                disabled={isDisabled || client.internalClient === 1 || power.value === ""}
                label="Valor"
                errorLabel="Valor Incorreto"
                inputcomponent={FormatMoney}
                required={false}
              />
            </Box>
          </Box>
        </Box>
      ) : null}

      {/* Ações */}
      <Box
        display="flex"
        sx={{
          mt: 2,
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        {isInternalProject && (
          <Button
            variant={sentToPosVenda ? "contained" : "outlined"}
            startIcon={<FactoryIcon />}
            sx={{
              width: isSmallScreen ? "100%" : "auto",
              borderColor: sentToPosVenda ? "#10b981" : "#CA9900",
              backgroundColor: sentToPosVenda ? "#10b981" : "transparent",
              color: sentToPosVenda ? "#fff" : "#CA9900",
              "&:hover": {
                borderColor: sentToPosVenda ? "#059669" : "#b38800",
                backgroundColor: sentToPosVenda ? "#059669" : "rgba(202, 153, 0, 0.05)",
              },
            }}
            disabled={isDisabled}
            onClick={handlePosVendaClick}
          >
            {sentToPosVenda ? "No Pós-Venda" : "Pós-Vendas"}
          </Button>
        )}
        
        <Button
          variant="outlined"
          color="error"
          disabled={isDisabled}
          onClick={handleClickOpen}
        >
          Excluir
        </Button>

        <CustomLoadingButton
          buttonText="Atualizar"
          buttonLoadingText="Atualizando..."
          disabled={isDisabled}
          handleButtonClick={handleUpdateClick}
          requestLoading={requestLoading}
        />
      </Box>
    </Box>
  );
}
